import { faSave } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import React, { useEffect, useMemo } from 'react';
import DomainForm, { DomainFormState, protocolOptions } from '../../../components/muiLegacy/Forms/DomainForm';
import VoxelFormPanel from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanel';
import VoxelFormPanelActionSubmit from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanelActionSubmit';
import VoxelPanelActions from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelPanelActions';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import * as yup from 'yup';
import VoxelFormPanelContent from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanelContent';

export interface ProjectDomainFormProps {
  project: ProjectGq;
  setDirty: (dirty: boolean) => void;
  reload: () => Promise<unknown>;
  setSave: (saveFunction: () => Promise<unknown>) => void;
}

const ProjectDomainForm: React.FC<ProjectDomainFormProps> = ({
  project,
  setDirty,
  reload,
  setSave,
}) => {
  // Find the correct domain
  const domain = project.domains.find((d) => d.type === DomainTypeGq.PROJECT);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        domain: yup.string().required('Required').min(1, 'Required'),
      }),
    []
  );

  // Form state
  const updateDomainMutation = projectsApiService.mutations.domains.useUpdateDomain();
  const createDomainMutation = projectsApiService.mutations.domains.useCreateDomains();
  const formikProps = useFormik<DomainFormState>({
    initialValues: {
      domain: domain ? domain.hostName : '',
      protocol: !!domain?.protocol?.length ? domain.protocol : protocolOptions[0].value,
      type: DomainTypeGq.PROJECT,
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      if (domain?.id !== undefined) {
        updateDomainMutation({
          projectId: project.id,
          id: domain.id,
          protocol: values.protocol,
          domain: values.domain,
          type: values.type,
        }).then(() => reload())
      } else {
        createDomainMutation({
          projectId: project.id,
          domains: [{ domain: values.domain, protocol: values.protocol, type: values.type }]
        }).then(() => reload())
      }
    }
  });

  useEffect(() => setSave(formikProps.submitForm), [formikProps.submitForm]);
  useEffect(() => {
    setDirty(formikProps.dirty);
    return () => setDirty(false);
  }, [formikProps.dirty]);

  return (
    <VoxelFormPanel
      title={<span>Project domain</span>}
      subtitle={
        <>
          A project domain is required for rank based functionality, such as analyses, content plan
          and rank tracking.
        </>
      }
    >
      <VoxelFormPanelContent>
        <div className="-mt-2 w-full flex flex-col gap-4">
          <DomainForm
            showLabels={true}
            protocolValue={formikProps.values.protocol}
            setProtocolValue={(value) => formikProps.setFieldValue('protocol', value)}
            domainValue={formikProps.values.domain}
            setDomainFormState={(valueFunc) => formikProps.setValues(d => { return valueFunc(d) })}
            deleteDomainValue={() => formikProps.setFieldValue('domain', '')}
            placeholder={'www.organization.com/se-sv'}
            disabled={formikProps.isSubmitting}
            showHelperText={formikProps.errors?.domain?.length > 0 && formikProps.submitCount > 0}
            helperText={
              !!formikProps.touched.domain
                ? formikProps.errors.domain
                : 'The exact text will be matched against SERP URLs to check keyword rank performance'
            }
          />
        </div>
      </VoxelFormPanelContent>
      <VoxelPanelActions>
        <VoxelFormPanelActionSubmit
          title={'Save changes'}
          onClick={formikProps.submitForm}
          disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
          endIcon={<FontAwesomeIcon icon={faSave} />}
        />
      </VoxelPanelActions>
    </VoxelFormPanel>
  );
};

export default ProjectDomainForm;
